import React from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Grid,
  Tooltip,
  Chip
} from '@mui/material';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import { FaChevronDown, FaChevronUp, FaComment, FaThList } from 'react-icons/fa';
import CompetencyDisplay from './CompetencyDisplay';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import { COLORS, EMPTY_UUID } from '../../../constants/radar';
import { REVIEW_TYPES } from '../../../constants/reviewTypes';
import {
  formatTemplateType,
  getReviewerCompetencyLevels,
  hasValidComments,
  hasValidSegmentComments,
  hasMeaningfulComment,
  getCompetencyColor,
  getCompetencyLevelName,
  getGaugeMaxValue
} from '../../../utils/feedbackReportUtils';
import { getNumericLevel } from '../../../constants/skillLevels';

const SkillsTable = ({
  skillsMap,
  sortedReviewers,
  expandableStates,
  formatTemplateType: formatTemplateTypeProp,
  reviewType
}) => {
  const {
    expandedSkills,
    expandedComments,
    expandedSegmentComments,
    toggleSkillExpand,
    toggleCommentsExpand,
    toggleSegmentCommentsExpand
  } = expandableStates;

  return (
    <TableContainer component={Paper} sx={{ mb: 4 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width="15%">Skill</TableCell>
            <TableCell width="15%">Description</TableCell>
            <TableCell width="10%" align="center">Overall</TableCell>
            {sortedReviewers.map((reviewer, index) => (
              <TableCell key={reviewer.id} align="center">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <EmployeeAvatar employee={reviewer.employee} size={32} sx={{ mb: 1 }} />
                  <Typography variant="subtitle2">
                    {reviewer.reviewerType === 'self' ? 'Self Review' : `Reviewer ${index + 1}`}
                  </Typography>
                  {reviewer.employee.id !== EMPTY_UUID && (
                    <Typography variant="body2">
                      {reviewer.employee?.fullName || 'N/A'}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {formatTemplateType(reviewer.skillsMatrixTemplate?.type)}
                  </Typography>
                </Box>
              </TableCell>
            ))}
            <TableCell width="5%"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from(skillsMap.values()).map(({ skill, isRequired, reviewerResponses, segmentMappings }) => (
            <SkillRow
              key={skill?.id || 'unknown-skill'}
              skill={skill}
              isRequired={isRequired}
              reviewerResponses={reviewerResponses}
              segmentMappings={segmentMappings}
              sortedReviewers={sortedReviewers}
              expandedSkills={expandedSkills}
              expandedComments={expandedComments}
              expandedSegmentComments={expandedSegmentComments}
              toggleSkillExpand={toggleSkillExpand}
              toggleCommentsExpand={toggleCommentsExpand}
              toggleSegmentCommentsExpand={toggleSegmentCommentsExpand}
              reviewType={reviewType}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Calculate skill average across all reviewers
const calculateSkillAverage = (reviewerResponses, segmentMappings, sortedReviewers, reviewType, isRequired = true) => {
  // Skip calculation for optional skills
  if (!isRequired) {
    let skillLevels = [];
    sortedReviewers.forEach(reviewer => {
      const reviewerSkillLevels = getReviewerCompetencyLevels(reviewer);
      if (reviewerSkillLevels.length > 0) {
        skillLevels = reviewerSkillLevels;
      }
    });
    return {
      average: null,
      count: 0,
      skillLevels,
      maxLevel: getGaugeMaxValue(skillLevels, reviewType),
      isOptional: true
    };
  }

  let totalCompetency = 0;
  let count = 0;
  let skillLevels = [];

  sortedReviewers.forEach(reviewer => {
    const response = reviewerResponses.get(reviewer?.id);
    const reviewerSkillLevels = getReviewerCompetencyLevels(reviewer);

    if (reviewerSkillLevels.length > 0) {
      skillLevels = reviewerSkillLevels; // Use any reviewer's skill levels as reference
    }

    if (response) {
      const reviewerSegmentMappings = segmentMappings.get(reviewer?.id) || [];

      // If there are segment mappings, use those
      if (reviewerSegmentMappings.length > 0) {
        reviewerSegmentMappings.forEach(segmentMapping => {
          if (segmentMapping?.competencyLevelId) {
            const competencyValue = getNumericLevel(segmentMapping.competencyLevelId, reviewerSkillLevels);
            const shouldConsiderStats = (
              (reviewType === REVIEW_TYPES.PARALLEL && competencyValue > 0)
              || reviewType === REVIEW_TYPES.SEQUENTIAL
            );

            totalCompetency += competencyValue;
            count += shouldConsiderStats ? 1 : 0;
          }
        });
      } else if (response?.competencyLevelId) {
        // Fallback to main response competency level
        const competencyValue = getNumericLevel(response.competencyLevelId, reviewerSkillLevels);
        const shouldConsiderStats = (
          (reviewType === REVIEW_TYPES.PARALLEL && competencyValue > 0)
          || reviewType === REVIEW_TYPES.SEQUENTIAL
        );

        totalCompetency += competencyValue;
        count += shouldConsiderStats ? 1 : 0;
      }
    }
  });

  const average = count > 0 ? Math.round((totalCompetency / count) * 10) / 10 : 0;

  return {
    average,
    count,
    skillLevels,
    maxLevel: getGaugeMaxValue(skillLevels, reviewType),
    isOptional: false
  };
};

const SkillOverallGauge = ({ reviewerResponses, segmentMappings, sortedReviewers, reviewType, isRequired }) => {
  const { average, count, skillLevels, maxLevel, isOptional } = calculateSkillAverage(
    reviewerResponses,
    segmentMappings,
    sortedReviewers,
    reviewType,
    isRequired
  );

  if (isOptional) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
        N/A
      </Typography>
    );
  }

  if (count === 0) {
    return (
      <Typography variant="body2" color="text.secondary" align="center">
        No data
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Gauge
        value={average}
        valueMin={0}
        valueMax={maxLevel}
        sx={{
          width: 80,
          height: 80,
          [`& .${gaugeClasses.valueText}`]: {
            fontSize: '1rem',
            fontWeight: 'bold',
          },
          [`& .${gaugeClasses.valueArc}`]: {
            fill: getCompetencyColor(average, skillLevels, reviewType),
          },
        }}
        text={`${average}`}
        startAngle={-110}
        endAngle={110}
      />
      <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
        {getCompetencyLevelName(average, skillLevels)}
      </Typography>
      <Typography variant="caption" color="text.secondary" align="center">
        ({count} {count === 1 ? 'assessment' : 'assessments'})
      </Typography>
    </Box>
  );
};

const ReviewerCompetencyCell = ({ reviewer, reviewerResponses, segmentMappings }) => {
  const hasResponse = reviewerResponses.has(reviewer?.id);
  const response = reviewerResponses.get(reviewer?.id);
  const reviewerSegmentMappings = segmentMappings.get(reviewer?.id) || [];
  
  // If there's no response at all
  if (!hasResponse) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not reviewed
      </Typography>
    );
  }
  
  // If there are segment mappings, check their completion status
  if (reviewerSegmentMappings.length > 0) {
    const completedSegments = reviewerSegmentMappings.filter(
      mapping => mapping?.competencyLevelId
    );
    const totalSegments = reviewerSegmentMappings.length;
    const completionRate = completedSegments.length / totalSegments;
    
    if (completionRate === 1) {
      // All segments are completed - show "Reviewed" only
      return (
        <Typography variant="body2" color="success.main">
          Reviewed
        </Typography>
      );
    } else if (completionRate > 0) {
      // Some segments are completed - show "Partially Reviewed"
      return (
        <Typography variant="body2" color="warning.main">
          Partially Reviewed ({completedSegments.length}/{totalSegments})
        </Typography>
      );
    } else {
      // No segments are completed
      return (
        <Typography variant="body2" color="text.secondary">
          Not reviewed
        </Typography>
      );
    }
  }
  
  // If there are no segment mappings but there is a response, show the competency
  if (response?.competencyLevelId) {
    return (
      <CompetencyDisplay 
        competencyLevelId={response?.competencyLevelId}
        skillLevels={getReviewerCompetencyLevels(reviewer)}
      />
    );
  }
  
  // Default case
  return (
    <Typography variant="body2" color="text.secondary">
      Not reviewed
    </Typography>
  );
};

const SkillRow = ({
  skill,
  isRequired = true,
  reviewerResponses,
  segmentMappings,
  sortedReviewers,
  expandedSkills,
  expandedComments,
  expandedSegmentComments,
  toggleSkillExpand,
  toggleCommentsExpand,
  toggleSegmentCommentsExpand,
  reviewType
}) => {
  return (
    <>
      <TableRow>
        <TableCell component="th" scope="row" sx={{ color: COLORS.PRIMARY }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {skill?.name || 'Unknown Skill'}
            {!isRequired && (
              <Chip
                label="Optional"
                size="small"
                sx={{
                  height: '20px',
                  fontSize: '10px',
                  backgroundColor: '#FFF3E0',
                  color: '#E65100'
                }}
              />
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ cursor: 'pointer' }}>
          <Tooltip title={skill?.description || ''} arrow placement="top">
            <Typography
              sx={{
                maxWidth: '300px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                display: '-webkit-box',
                lineHeight: '1.2em',
                maxHeight: '3.6em',
                fontSize: '0.875rem',
                color: 'text.secondary'
              }}
            >
              {skill?.description || ''}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell align="center">
          <SkillOverallGauge
            reviewerResponses={reviewerResponses}
            segmentMappings={segmentMappings}
            sortedReviewers={sortedReviewers}
            reviewType={reviewType}
            isRequired={isRequired}
          />
        </TableCell>
                 {sortedReviewers.map(reviewer => (
           <TableCell key={reviewer?.id || 'unknown-reviewer'} align="center">
             <ReviewerCompetencyCell
               reviewer={reviewer}
               reviewerResponses={reviewerResponses}
               segmentMappings={segmentMappings}
             />
           </TableCell>
         ))}
        <TableCell>
          <ActionButtons
            skill={skill}
            segmentMappings={segmentMappings}
            reviewerResponses={reviewerResponses}
            expandedSkills={expandedSkills}
            expandedComments={expandedComments}
            toggleSkillExpand={toggleSkillExpand}
            toggleCommentsExpand={toggleCommentsExpand}
          />
        </TableCell>
      </TableRow>
      
      <SegmentDetailsRow
        skill={skill}
        segmentMappings={segmentMappings}
        sortedReviewers={sortedReviewers}
        expandedSkills={expandedSkills}
        expandedSegmentComments={expandedSegmentComments}
        toggleSegmentCommentsExpand={toggleSegmentCommentsExpand}
      />

      <CommentsRow
        skill={skill}
        reviewerResponses={reviewerResponses}
        sortedReviewers={sortedReviewers}
        expandedComments={expandedComments}
      />
    </>
  );
};

const ActionButtons = ({
  skill,
  segmentMappings,
  reviewerResponses,
  expandedSkills,
  expandedComments,
  toggleSkillExpand,
  toggleCommentsExpand
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {Array.from(segmentMappings.values()).some(mappings => mappings.length > 0) && (
        <Tooltip title="View Skills" placement="left">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FaThList 
              size={12} 
              color={COLORS.GRAY} 
              style={{ marginRight: '4px' }}
            />
            <IconButton 
              size="small" 
              onClick={() => toggleSkillExpand(skill?.id)}
              aria-label="expand row"
              sx={{ 
                color: expandedSkills[skill?.id] ? COLORS.INFO : 'inherit',
              }}
            >
              {expandedSkills[skill?.id] ? <FaChevronUp /> : <FaChevronDown />}
            </IconButton>
          </Box>
        </Tooltip>
      )}
      
      {hasValidComments(reviewerResponses) && (
        <Tooltip title="View Comments" placement="left">
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <FaComment 
              size={12} 
              color={COLORS.GRAY} 
              style={{ marginRight: '4px' }}
            />
            <IconButton 
              size="small" 
              onClick={() => toggleCommentsExpand(skill?.id)}
              aria-label="expand comments"
              sx={{ 
                color: expandedComments[skill?.id] ? COLORS.INFO : 'inherit',
              }}
            >
              {expandedComments[skill?.id] ? <FaChevronUp /> : <FaChevronDown />}
            </IconButton>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

const SegmentDetailsRow = ({
  skill,
  segmentMappings,
  sortedReviewers,
  expandedSkills,
  expandedSegmentComments,
  toggleSegmentCommentsExpand
}) => {
  const hasSegments = Array.from(segmentMappings.values()).some(mappings => mappings.length > 0);

  if (!hasSegments) return null;

  return (
    <TableRow>
      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={sortedReviewers.length + 4}>
        <Collapse in={expandedSkills[skill?.id]} timeout="auto" unmountOnExit>
          <Box sx={{ margin: 2 }}>
            <Typography variant="subtitle1" gutterBottom component="div">
              Technologies for {skill?.name || 'Unknown Skill'}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Technology</TableCell>
                  {sortedReviewers.map((reviewer, index) => (
                    <TableCell key={reviewer.id} align="center">
                      <Typography variant="subtitle2">
                        {reviewer.reviewerType === 'self' ? 'Self' : `Reviewer ${index + 1}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {formatTemplateType(reviewer.skillsMatrixTemplate?.type)}
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell width="5%"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from(new Set(
                  Array.from(segmentMappings.values())
                    .flatMap(mappings => mappings.map(m => m?.segment?.id))
                )).map(segmentId => (
                  <SegmentRow
                    key={segmentId || 'unknown-segment'}
                    segmentId={segmentId}
                    segmentMappings={segmentMappings}
                    sortedReviewers={sortedReviewers}
                    expandedSegmentComments={expandedSegmentComments}
                    toggleSegmentCommentsExpand={toggleSegmentCommentsExpand}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

const SegmentRow = ({
  segmentId,
  segmentMappings,
  sortedReviewers,
  expandedSegmentComments,
  toggleSegmentCommentsExpand
}) => {
  const segmentMapping = Array.from(segmentMappings.values())
    .flatMap(mappings => mappings)
    .find(m => m?.segment?.id === segmentId);
  
  const segmentName = segmentMapping?.segment?.segment?.name || 'Unknown';

  return (
    <>
      <TableRow>
        <TableCell component="th" scope="row">
          {segmentName}
        </TableCell>
        {sortedReviewers.map(reviewer => {
          const mapping = segmentMappings.get(reviewer?.id)?.find(
            m => m?.segment?.id === segmentId
          );
          return (
            <TableCell key={reviewer?.id || 'unknown-reviewer'} align="center">
              {mapping ? (
                <CompetencyDisplay 
                  competencyLevelId={mapping?.competencyLevelId}
                  skillLevels={getReviewerCompetencyLevels(reviewer)}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              )}
            </TableCell>
          );
        })}
        <TableCell>
          {hasValidSegmentComments(segmentMappings, segmentId) && (
            <Tooltip title="View Comments" placement="left">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FaComment 
                  size={12} 
                  color={COLORS.GRAY} 
                  style={{ marginRight: '4px' }}
                />
                <IconButton 
                  size="small" 
                  onClick={() => toggleSegmentCommentsExpand(segmentId)}
                  aria-label="expand segment comments"
                  sx={{ 
                    color: expandedSegmentComments[segmentId] ? COLORS.INFO : 'inherit',
                  }}
                >
                  {expandedSegmentComments[segmentId] ? <FaChevronUp /> : <FaChevronDown />}
                </IconButton>
              </Box>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      
      <SegmentCommentsRow
        segmentId={segmentId}
        segmentName={segmentName}
        segmentMappings={segmentMappings}
        sortedReviewers={sortedReviewers}
        expandedSegmentComments={expandedSegmentComments}
      />
    </>
  );
};

const SegmentCommentsRow = ({
  segmentId,
  segmentName,
  segmentMappings,
  sortedReviewers,
  expandedSegmentComments
}) => {
  const hasComments = hasValidSegmentComments(segmentMappings, segmentId);

  if (!hasComments) return null;

  return (
    <TableRow>
      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={sortedReviewers.length + 3}>
        <Collapse in={expandedSegmentComments[segmentId]} timeout="auto" unmountOnExit>
          <Box sx={{ margin: 2 }}>
            <Typography variant="subtitle2" gutterBottom component="div">
              Comments for {segmentName}
            </Typography>
            <CommentsGrid
              sortedReviewers={sortedReviewers}
              getEntry={(reviewer) => segmentMappings.get(reviewer?.id)?.find(
                m => m?.segment?.id === segmentId
              )}
              noCommentsMessage="No comments available for this technology."
            />
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

const CommentsRow = ({ skill, reviewerResponses, sortedReviewers, expandedComments }) => {
  const hasComments = hasValidComments(reviewerResponses);

  if (!hasComments) return null;

  return (
    <TableRow>
      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={sortedReviewers.length + 4}>
        <Collapse in={expandedComments[skill?.id]} timeout="auto" unmountOnExit>
          <Box sx={{ margin: 2 }}>
            <CommentsGrid
              sortedReviewers={sortedReviewers}
              getEntry={(reviewer) => reviewerResponses.get(reviewer?.id)}
              noCommentsMessage="No comments available for this skill."
            />
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

// One card per reviewer who rated this item, in reviewer order, so the
// "Reviewer N" slots never shift. A rated item with a blank (empty or
// whitespace-only) comment gets an explicit "No comment provided" card instead
// of disappearing — a missing card reads as lost data.
const CommentsGrid = ({ sortedReviewers, getEntry, noCommentsMessage }) => {
  const hasAnyComments = sortedReviewers.some((reviewer) =>
    hasMeaningfulComment(getEntry(reviewer)?.comments)
  );

  return (
    <Grid container spacing={2} sx={{
      width: '100%',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        md: 'repeat(2, 1fr)',
        lg: 'repeat(3, 1fr)'
      },
      gap: '16px',
      '& .MuiGrid-item': {
        padding: '0',
        width: '100%'
      }
    }}>
      {sortedReviewers.map((reviewer, index) => {
        const entry = getEntry(reviewer);

        // Not rated (no answer, or an optional item skipped) → no card.
        if (!entry?.competencyLevelId) {
          return null;
        }

        const comments = entry.comments;
        const hasComment = hasMeaningfulComment(comments);

        return (
          <Grid item xs={12} md={6} lg={4} key={reviewer?.id || `reviewer-${index}`}>
            <Box 
              sx={{ 
                p: 2, 
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                backgroundColor: '#f9f9f9',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                height: '100%'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle2">
                    {reviewer.reviewerType === 'self' ? 'Self Review' : `Reviewer ${index + 1}`}
                  </Typography>
                </Box>
              </Box>
              {hasComment ? (
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    pl: 1.5,
                    py: 0.5,
                    mt: 1,
                    backgroundColor: 'white',
                    borderRadius: '0 4px 4px 0'
                  }}
                >
                  {comments}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: 'italic', pl: 1.5, py: 0.5, mt: 1 }}
                >
                  No comment provided.
                </Typography>
              )}
            </Box>
          </Grid>
        );
      })}
      {!hasAnyComments && (
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" align="center">
            {noCommentsMessage}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default SkillsTable;